import type { ClientCommunityWithLevel } from "api-types/communities/communitiesResponses";
import type { ClientHomePlanBaseData } from "api-types/homePlans/homePlanResponses";
import type { ClientLocationsPortalData } from "api-types/locations/locationsResponses";
import type { ClientEstimationTaskHydrated } from "api-types/tasks/estimationTaskResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import { FetchErrorMessage } from "lib/components/errors/FetchErrorMessage";
import { getBuilderEstimationTaskURL, getBuilderLocationsURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { locationDefaultPath, locationsPath, useAppRouterParams } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import type { PropsWithChildren, ReactNode, ToggleEventHandler } from "react";
import { useEffect, useMemo } from "react";
import Link from "ui/components/Link";
import Loading from "ui/components/Loading";
import cx from "ui/cx";
import useSessionStorage from "ui/hooks/useSessionStorage";
import IconChevronFilled from "ui/icons/IconChevronFilled";
import { canViewChildLocations, getLocationLotCountWithDescendants } from "./communities/utils/locationUtils";
import { getLocationIcon, getLocationIconForType } from "./getLocationIcon";
import styles from "./Locations.module.scss";

interface LocationNodeType {
  id: string;
  name: string;
  icon: ReactNode;
  link: string;
  selected: boolean;
  children: LocationNodeType[];
}

function shouldShowLocation(args: {
  location: ClientCommunityWithLevel;
  canViewChildLocations: boolean;
  hasAuthorizedParent: boolean;
}): boolean {
  const { location, canViewChildLocations, hasAuthorizedParent } = args;

  if (location.archivedAt) {
    return false;
  }

  if (!canViewChildLocations && hasAuthorizedParent) {
    return false;
  }

  return true;
}

const ALL_LOCATIONS_ID = "locations";

interface LocationsProps {
  builderId: string;
  selected: boolean;
}

export function Locations({ builderId, selected: isLocationsRoute }: LocationsProps) {
  const { locationId, homePlanId, taskId } = useAppRouterParams();
  const { builder, hasPermission, enabledFeatures } = useUserContext();
  const [openNodeIds, setOpenNodeIds] = useSessionStorage<string[]>(`_higharcOpenLocations-${builderId}`, [
    ALL_LOCATIONS_ID,
  ]);

  const { data: homePlanBaseData } = useFetch<ClientHomePlanBaseData>(
    `${WEB_API_BASE_PATH}/builders/${builderId}/home-plans/${homePlanId}/base-data`,
    [builderId, homePlanId],
    { handleErrorLocally: true },
  );

  const { data: estimationTask } = useFetch<ClientEstimationTaskHydrated>(
    taskId ? getBuilderEstimationTaskURL({ builderId, taskId }) : "",
    [builderId, taskId],
    { handleErrorLocally: true },
  );

  const { data, error, loading } = useFetch<ClientLocationsPortalData>(
    getBuilderLocationsURL({ builderId }),
    [builderId],
    {
      handleErrorLocally: true,
    },
  );

  const normalizedLocationId = homePlanBaseData?.community?.id ?? estimationTask?.locationId ?? locationId;

  // Clean out stale community IDs
  useEffect(() => {
    if (data) {
      const filteredIds = openNodeIds.filter(
        (id) => id === ALL_LOCATIONS_ID || data.locations.some((community) => id === community.id),
      );
      if (openNodeIds.length !== filteredIds.length) {
        setOpenNodeIds(filteredIds);
      }
    }
  }, [data]); // Only re-run if the data changes

  // Expand tree to currently visited location
  useEffect(() => {
    if (data && normalizedLocationId && !openNodeIds.includes(normalizedLocationId)) {
      const nodeIds = [ALL_LOCATIONS_ID, normalizedLocationId];
      let currentNodeId: string | null | undefined = normalizedLocationId;
      while (currentNodeId) {
        const currentNode = data.locations.find((c) => c.id === currentNodeId);
        if (currentNode?.parentId) {
          nodeIds.push(currentNode.parentId);
        }
        currentNodeId = currentNode?.parentId;
      }
      setOpenNodeIds([...new Set(openNodeIds.concat(nodeIds))]);
    }
  }, [data, normalizedLocationId]); // Only re-run if the data or location changes

  const locationDescendantsLotsCount = useMemo(() => {
    if (!data?.locations) {
      return;
    }
    return getLocationLotCountWithDescendants(data.locations);
  }, [data]);

  // Construct tree
  const locationTree = useMemo(() => {
    if (!data?.locations) {
      return [];
    }

    const tree: LocationNodeType[] = [];
    const locationsById = Object.fromEntries(
      data.locations.map((c) => [c.id, { ...c, children: [] as LocationNodeType[] }]) ?? [],
    );

    for (const node of Object.values(locationsById).sort((a, b) => b.depth - a.depth)) {
      const hasAuthorizedParent = !!(
        node.parentId &&
        locationsById[node.parentId] &&
        !locationsById[node.parentId].archivedAt
      );

      const canViewChildLocationsOnParent = node.parentId
        ? hasPermission("VIEW_CHILD_LOCATIONS", { resourceId: node.parentId })
        : false;

      if (
        !shouldShowLocation({
          location: node,
          canViewChildLocations: canViewChildLocationsOnParent,
          hasAuthorizedParent,
        })
      ) {
        continue;
      }

      const newNode = {
        id: node.id,
        name: node.name,
        icon: getLocationIconForType(node.locationLevel?.type, builder?.locationSettings),
        link: locationDefaultPath(
          builderId,
          node.id,
          node.locationLevel?.type,
          node.disableLots,
          !!locationDescendantsLotsCount?.[node.id],
          hasPermission("VIEW_LOTS"),
          canViewChildLocations(
            node.locationLevel?.type,
            builder?.locationSettings,
            hasPermission("VIEW_CHILD_LOCATIONS", { resourceId: node.id }),
          ),
          enabledFeatures.estimationTasks,
        ),
        selected: node.id === normalizedLocationId,
        children: node.children,
      };

      if (!node.parentId) {
        tree.push(newNode);
      } else {
        const parent = locationsById[node.parentId];
        if (parent) {
          parent.children.push(newNode);
        } else {
          tree.push(newNode);
        }
      }
    }

    return tree.sort((a, b) => a.name.localeCompare(b.name));
  }, [
    data,
    builderId,
    normalizedLocationId,
    builder?.locationSettings,
    locationDescendantsLotsCount,
    hasPermission,
    enabledFeatures.estimationTasks,
  ]);

  // Mock node for "All Locations" node
  const allLocationsNode = useMemo(
    () => ({
      id: ALL_LOCATIONS_ID,
      name: "Locations",
      icon: getLocationIcon(),
      link: locationsPath(builderId),
      selected: isLocationsRoute && !normalizedLocationId,
      children: [],
    }),
    [builderId, isLocationsRoute, normalizedLocationId],
  );

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <FetchErrorMessage fetchError={error} />;
  }

  return (
    <div className={styles.wrapper}>
      <LocationNode node={allLocationsNode} {...{ openNodeIds, setOpenNodeIds }}>
        {Object.values(locationTree).map((location) => (
          <LocationNodes
            key={`location-node-${location.id}`}
            currentNode={location}
            {...{ openNodeIds, setOpenNodeIds }}
          />
        ))}
      </LocationNode>
    </div>
  );
}

function LocationNodes({
  currentNode,
  openNodeIds,
  setOpenNodeIds,
}: {
  currentNode: LocationNodeType;
  openNodeIds: string[];
  setOpenNodeIds: (input: string[]) => void;
}) {
  return (
    <LocationNode node={currentNode} {...{ openNodeIds, setOpenNodeIds }}>
      {currentNode.children
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((child) => (
          <LocationNodes
            key={`location-node-key-${child.id}`}
            currentNode={child}
            {...{ openNodeIds, setOpenNodeIds }}
          />
        ))}
    </LocationNode>
  );
}

interface LocationNodeProps {
  node: LocationNodeType;
  openNodeIds: string[];
  setOpenNodeIds: (input: string[]) => void;
}

function LocationNode({ node, openNodeIds, setOpenNodeIds, children }: PropsWithChildren<LocationNodeProps>) {
  const { id, selected, link, icon, name } = node;
  const noChildren = node.id !== ALL_LOCATIONS_ID && node.children.length === 0;
  const isOpen = openNodeIds.includes(id);

  const handleToggle: ToggleEventHandler<HTMLDetailsElement> = (e) => {
    // Close
    if (!e.currentTarget.open) {
      // Close all nodes
      if (id === ALL_LOCATIONS_ID) {
        return setOpenNodeIds([]);
      }
      // Close specific node, close children as well
      return setOpenNodeIds(
        openNodeIds.filter((openNodeId) => openNodeId !== id && !node.children.some((c) => c.id === openNodeId)),
      );
    }
    // Open, ensure not double-counting
    if (!isOpen) {
      return setOpenNodeIds([...openNodeIds, id]);
    }
  };

  return (
    <details onToggle={handleToggle} className={cx({ styles, selected })} open={isOpen}>
      <summary>
        <span className={cx(styles.toggle, { styles, isOpen, noChildren })}>
          <IconChevronFilled />
        </span>
        <Link
          href={link}
          title={name}
          onClick={() => {
            // Also open on navigate request
            if (!isOpen) {
              setOpenNodeIds([...openNodeIds, id]);
            }
          }}
        >
          {icon} <span>{name}</span>
        </Link>
      </summary>
      {children}
    </details>
  );
}
